// Task lifecycle + streaming client.
//
// Lifted out of `../api` so the file boundary matches the backend
// `src/railyard/routes/tasks.py`. Functions here drive
// `POST /api/tasks/*` and consume the SSE streams the server emits.
//
// All HTTP plumbing (`apiFetch`, `requestJson`, `requestOk`, the SSE
// consumer, error types) still lives in `../api` — this file imports
// what it needs and re-exports nothing infrastructure-shaped, only the
// task functions and the share-token types declared alongside them.

import type { components } from "@/lib/api-types";
import { logger } from "@/lib/log";
import { track } from "@/lib/analytics";
import { NO_WEB_SEARCH_DIRECTIVE } from "@/components/tasks/task-prefs";

import {
  API,
  apiFetch,
  consumeSse,
  isAbortError,
  requestJson,
  requestOk,
  type AgentEvent,
  type ChatStreamHandlers,
  type Task,
  type TaskRunStatus,
  type TaskSummary,
  errorMessage,
} from "../api";
import { consumeSeedSource } from "@/lib/task-seed";


/** Convenience alias — every server schema lives under components["schemas"]. */
type Schemas = components["schemas"];


const log = logger("api/tasks");


// Emit the analytics `message_sent` event from the actual send path, after the
// POST is accepted. Both message routes (the task-composer streaming send and
// the project-home first-turn send) funnel through here, so every user message
// is counted exactly once — and only after the attempt cleared its guards and
// the request was issued, never on a dropped no-op.
function trackMessageSent(
  taskId: string,
  wireText: string,
  opts: {
    permissionMode?: string;
    model?: string;
    effort?: string;
    attachmentFileIds?: string[];
    webSearch?: boolean;
    source?: string;
    projectId?: string | null;
  },
) {
  // `wireText` carries the NO_WEB_SEARCH_DIRECTIVE prefix when web search was
  // toggled off for this turn. Strip it so char_count reflects what the user
  // actually wrote. Prefer the caller's explicit web_search state (it knows
  // when admin has disabled web tools, where no directive is sent at all); fall
  // back to inferring from the directive prefix.
  const noWeb = wireText.startsWith(NO_WEB_SEARCH_DIRECTIVE);
  const userText = noWeb ? wireText.slice(NO_WEB_SEARCH_DIRECTIVE.length) : wireText;
  // A seeded-but-not-autosubmitted draft (a composer deep link) labels the
  // first message the user sends themselves; the marker is one-shot.
  const source = opts.source ?? consumeSeedSource(taskId) ?? "composer";
  const projectId = opts.projectId ?? null;
  track("message_sent", {
    task_id: taskId,
    project_id: projectId,
    permission_mode: opts.permissionMode ?? "default",
    model: opts.model ?? null,
    effort: opts.effort ?? null,
    web_search: opts.webSearch ?? !noWeb,
    attachment_count: opts.attachmentFileIds?.length ?? 0,
    char_count: userText.length,
    source,
  });
  // A plan approval resumes a drafted plan via a normal send carrying
  // source="plan_approval". Emit plan_approved from this accepted path (not on
  // the approve click) so the plan->execution funnel ignores resumes the server
  // rejected (rate limit / plan cap / run-in-progress).
  if (source === "plan_approval") {
    track("plan_approved", { task_id: taskId, project_id: projectId });
  }
}


export async function listTasks(): Promise<TaskSummary[]> {
  return requestJson(`${API}/api/tasks`);
}

export async function createTask(opts?: {
  projectId?: string;
  title?: string;
  disabledConnectors?: string[];
}): Promise<Task> {
  const body: Record<string, unknown> = {};
  if (opts?.projectId) body.project_id = opts.projectId;
  if (opts?.title) body.title = opts.title;
  if (opts?.disabledConnectors) body.disabled_connectors = opts.disabledConnectors;
  const task: Task = await requestJson(`${API}/api/tasks`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  track("task_created", {
    task_id: task.id,
    project_id: task.project_id ?? null,
    source: opts?.projectId ? "project" : "default",
  });
  return task;
}

export async function getTask(id: string): Promise<Task> {
  return requestJson(`${API}/api/tasks/${encodeURIComponent(id)}`);
}

export type TaskHistoryPage = Schemas["TaskHistory"];
export type TaskHistoryRecord = Schemas["TaskHistoryRecord"];

/**
 * A page of a task's conversation from the durable transcript, newest page
 * first — pass the previous page's `next_before` to walk further back.
 *
 * Separate from `getTask` on purpose (ENG-4946): that endpoint already reads a
 * ~1 MiB document and had its read amplification deliberately removed, while a
 * history page costs a prefix listing plus one object read per record. This
 * loads on demand.
 */
export async function getTaskHistory(
  taskId: string,
  opts?: { before?: string | null; limit?: number },
): Promise<TaskHistoryPage> {
  const q = new URLSearchParams();
  if (opts?.before) q.set("before", opts.before);
  if (opts?.limit) q.set("limit", String(opts.limit));
  const qs = q.toString();
  return requestJson(
    `${API}/api/tasks/${encodeURIComponent(taskId)}/history${qs ? `?${qs}` : ""}`,
  );
}

/** Task-page presence heartbeat (ENG-2325): tells the server the owner is
 *  looking at this task (or, with `left`, that the tab just went hidden), so
 *  the "deliverable is ready" email holds off. Resolves false on a 404 (not
 *  the caller's task) so the caller can stop beating. */
export async function postTaskPresence(taskId: string, left = false): Promise<boolean> {
  const r = await requestOk(
    `${API}/api/tasks/${encodeURIComponent(taskId)}/presence${left ? "?left=true" : ""}`,
    { method: "POST", keepalive: left },
    { allowStatus: [404] },
  );
  return r.status !== 404;
}

export async function deleteTask(id: string): Promise<void> {
  await requestOk(`${API}/api/tasks/${encodeURIComponent(id)}`, { method: "DELETE" });
}

export async function updateTaskProject(taskId: string, projectId: string): Promise<Task> {
  return requestJson(`${API}/api/tasks/${encodeURIComponent(taskId)}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ project_id: projectId }),
  });
}

/** Set the connectors switched off for a task. Server rejects this once the
 * task has turns (the MA session binds its connector set at creation). */
export async function updateTaskDisabledConnectors(
  taskId: string,
  disabledConnectors: string[],
): Promise<Task> {
  return requestJson(`${API}/api/tasks/${encodeURIComponent(taskId)}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ disabled_connectors: disabledConnectors }),
  });
}

export async function renameTask(taskId: string, title: string): Promise<Task> {
  return requestJson(`${API}/api/tasks/${encodeURIComponent(taskId)}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ title }),
  });
}

export type TaskShareStatus = Schemas["TaskShareResponse"];

export async function shareTask(taskId: string): Promise<TaskShareStatus> {
  return requestJson(`${API}/api/tasks/${encodeURIComponent(taskId)}/share`, {
    method: "POST",
  });
}

export async function unshareTask(taskId: string): Promise<TaskShareStatus> {
  return requestJson(`${API}/api/tasks/${encodeURIComponent(taskId)}/share`, {
    method: "DELETE",
  });
}

export type SharedTask = Schemas["SharedTask"];

export async function fetchSharedTask(token: string): Promise<SharedTask> {
  // Public endpoint — no auth headers; requestJson still attaches the
  // workspace header which the server simply ignores for /api/share/*.
  return requestJson(`${API}/api/share/${encodeURIComponent(token)}`);
}

/** Fire a task message without consuming the SSE stream.
 *
 * Returns once the response headers arrive — by then the server has already
 * appended the user turn synchronously and flipped `is_running=true`, so a
 * caller that immediately navigates can trust `useTaskSession`'s auto-resume
 * to attach to the live run. The server worker keeps running detached; any
 * later subscriber (resumeTaskStream) picks up events from index 0.
 *
 * Use this when starting a task and navigating elsewhere — `streamTaskMessage`
 * is the right call when the caller will actually render the stream itself.
 */
export async function sendTaskMessage(
  taskId: string,
  text: string,
  options?: {
    permissionMode?: string;
    attachmentFileIds?: string[];
    model?: string;
    effort?: string;
    commentSlides?: Schemas["CommentSlideRef"][];
    outcome?: Schemas["OutcomeRequest"] | null;
    verificationOptOut?: boolean;
    webSearch?: boolean;
    source?: string;
    projectId?: string | null;
  },
  signal?: AbortSignal,
): Promise<void> {
  const body: Record<string, unknown> = { text };
  if (options?.permissionMode) body.permission_mode = options.permissionMode;
  if (options?.attachmentFileIds && options.attachmentFileIds.length > 0) {
    body.attachment_file_ids = options.attachmentFileIds;
  }
  if (options?.model) body.model = options.model;
  if (options?.effort) body.effort = options.effort;
  // Opt-in MA outcome (e.g. the Verification grader applied via a project's
  // verification_default on the first turn from the project-home composer).
  if (options?.outcome) body.outcome = options.outcome;
  // Explicit opt-out of the server-side workspace/project Verification default
  // (the user toggled Verification off for this turn). Only sent when true.
  if (options?.verificationOptOut) body.verification_opt_out = true;
  if (options?.commentSlides && options.commentSlides.length > 0) {
    body.comment_slides = options.commentSlides;
  }
  const r = await apiFetch(`${API}/api/tasks/${encodeURIComponent(taskId)}/messages`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "text/event-stream" },
    body: JSON.stringify(body),
    signal,
  });
  if (r.ok) {
    // apiFetch resolves with non-OK responses (plan-cap 402, rate-limit, 409
    // run-in-progress); only count a send the server accepted.
    trackMessageSent(taskId, text, {
      permissionMode: options?.permissionMode,
      model: options?.model,
      effort: options?.effort,
      attachmentFileIds: options?.attachmentFileIds,
      webSearch: options?.webSearch,
      source: options?.source ?? "project_home",
      projectId: options?.projectId,
    });
  }
  // Drop the SSE stream — events accumulate server-side and the next
  // subscriber gets them from index 0.
  try {
    await r.body?.cancel();
  } catch {
    /* already closed */
  }
}

/** Send a message into a task; stream agent events back. Persists user +
 * assistant turn server-side. The task's `project_id` (set at creation,
 * stored on the thread record) determines what memory + sources + project
 * instructions are mounted on the underlying MA session — not a per-call
 * argument anymore.
 */
export async function streamTaskMessage(
  taskId: string,
  text: string,
  handlers: ChatStreamHandlers,
  permissionMode?: string,
  attachmentFileIds?: string[],
  model?: string,
  outcome?: Schemas["OutcomeRequest"] | null,
  signal?: AbortSignal,
  commentSlides?: Schemas["CommentSlideRef"][],
  verificationOptOut?: boolean,
  analytics?: { source?: string; webSearch?: boolean; projectId?: string | null },
  effort?: string,
): Promise<void> {
  try {
    const body: Record<string, unknown> = { text };
    if (permissionMode) {
      body.permission_mode = permissionMode;
    }
    if (attachmentFileIds && attachmentFileIds.length > 0) {
      body.attachment_file_ids = attachmentFileIds;
    }
    if (model) {
      body.model = model;
    }
    if (effort) {
      body.effort = effort;
    }
    // Opt-in MA outcome for this turn (define_outcome grader loop). The route
    // suppresses it in plan mode server-side; we also avoid sending one there.
    if (outcome) {
      body.outcome = outcome;
    }
    // Explicit opt-out of the server-side workspace/project Verification
    // default (user toggled Verification off). Only sent when true.
    if (verificationOptOut) {
      body.verification_opt_out = true;
    }
    // Rendered slides the user marked up — the route renders each to a PNG and
    // attaches it as an image content block so the agent sees the slide.
    if (commentSlides && commentSlides.length > 0) {
      body.comment_slides = commentSlides;
    }
    const r = await apiFetch(`${API}/api/tasks/${encodeURIComponent(taskId)}/messages`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "text/event-stream" },
      body: JSON.stringify(body),
      signal,
    });
    if (r.ok) {
      // apiFetch resolves with non-OK responses (plan-cap 402, rate-limit, 409
      // run-in-progress) and consumeSse surfaces the error; only count a send
      // the server accepted.
      trackMessageSent(taskId, text, {
        permissionMode,
        model,
        effort,
        attachmentFileIds,
        webSearch: analytics?.webSearch,
        source: analytics?.source,
        projectId: analytics?.projectId,
      });
    }
    return await consumeSse(r, handlers);
  } catch (e) {
    if (isAbortError(e)) return; // caller cancelled (unmount / task switch); not a failure
    handlers.onError(errorMessage(e, String(e)));
  }
}

/** Submit an answer to a pending AskUserQuestion and stream the agent's
 * continuation. The server appends the answer label as a user turn and
 * sends a custom_tool_result back into the MA session.
 */
export async function submitAnswer(
  taskId: string,
  toolUseId: string,
  answer: string,
  extraAnswers: Array<{ tool_use_id: string; answer: string }>,
  handlers: ChatStreamHandlers,
  signal?: AbortSignal,
  analytics?: {
    projectId?: string | null;
    permissionMode?: string;
    model?: string;
    effort?: string;
    webSearch?: boolean;
  },
  onAccepted?: () => void,
): Promise<void> {
  try {
    // Web and API deploy independently. An old API must reject a batch,
    // rather than ignore extra_answers and start a partial, deadlocked resume.
    const endpoint = extraAnswers.length ? "answers" : "answer";
    const r = await apiFetch(`${API}/api/tasks/${encodeURIComponent(taskId)}/${endpoint}`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "text/event-stream" },
      body: JSON.stringify({
        tool_use_id: toolUseId,
        answer,
        extra_answers: extraAnswers,
      }),
      signal,
    });
    if (r.ok) {
      onAccepted?.();
      // An accepted AskUserQuestion answer is a user turn server-side, so count
      // it for message / activity-volume metrics — segmented by source so it can
      // be split from composed messages in the funnel.
      trackMessageSent(taskId, answer, { source: "question_answer", ...analytics });
    }
    return await consumeSse(r, handlers);
  } catch (e) {
    if (isAbortError(e)) return;
    handlers.onError(errorMessage(e, String(e)));
  }
}

/** Submit allow/deny verdicts for tool calls paused by an always_ask
 * permission policy → resume the session → stream the continuation. The
 * whole batch is sent at once: MA keeps the session in requires_action
 * until every blocking tool call for the turn is resolved. */
export async function submitConfirmations(
  taskId: string,
  decisions: Array<{
    tool_use_id: string;
    result: "allow" | "deny";
    deny_message?: string;
  }>,
  handlers: ChatStreamHandlers,
  signal?: AbortSignal,
): Promise<void> {
  try {
    const r = await apiFetch(
      `${API}/api/tasks/${encodeURIComponent(taskId)}/confirm`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "text/event-stream",
        },
        body: JSON.stringify({ decisions }),
        signal,
      },
    );
    return await consumeSse(r, handlers);
  } catch (e) {
    if (isAbortError(e)) return;
    handlers.onError(errorMessage(e, String(e)));
  }
}

/** Resume an in-flight run. Backfills events from `since` then streams live.
 *  `tail` skips the backlog and streams only NEW events — used when the client
 *  has already seeded the in-flight turn from a fresh snapshot (navigate-away/
 *  back), so a replay would just re-animate content already on screen. */
export async function resumeTaskStream(
  taskId: string,
  handlers: ChatStreamHandlers,
  since = 0,
  signal?: AbortSignal,
  tail = false,
): Promise<void> {
  try {
    const r = await apiFetch(
      `${API}/api/tasks/${encodeURIComponent(taskId)}/stream?since=${since}${tail ? "&tail=true" : ""}`,
      { headers: { Accept: "text/event-stream" }, signal },
    );
    let localReplay = false;
    let fallback = false;
    return await consumeSse(r, {
      ...handlers,
      onEvent: (evt) => {
        if (fallback) return;
        if (evt.type === "stream_mode") {
          localReplay = evt.mode === "local";
          if (tail || localReplay) return;
        }
        // Old servers may have snapshot cursors but no stream capability
        // marker. Do not apply even their first frame before seeding + tailing.
        if (!tail && since === 0 && !localReplay && handlers.onReplayUnavailable) {
          fallback = true;
          handlers.onReplayUnavailable();
          return;
        }
        if (evt.type !== "stream_mode") handlers.onEvent(evt);
      },
      onDone: () => { if (!fallback) handlers.onDone(); },
    });
  } catch (e) {
    if (isAbortError(e)) return;
    handlers.onError(errorMessage(e, String(e)));
  }
}

/** Cheap probe: is a run currently active for this task? */
export async function getTaskRunStatus(taskId: string): Promise<TaskRunStatus> {
  return requestJson(`${API}/api/tasks/${encodeURIComponent(taskId)}/run`);
}

/** Signal cooperative cancellation of an in-flight task run.
 *
 * Server flips a flag the runner checks between events. In-flight tool
 * calls still complete (Python threads can't be safely interrupted) but
 * no further events reach the client.
 */
export async function prewarmTask(
  taskId: string,
  options?: { model?: string; effort?: string },
): Promise<void> {
  // Best-effort: fired when the user starts typing to hide MA session-create
  // latency. Silently swallow errors — the real send will create the session
  // if this fails. `effort` is the composer's selected level so the warmed
  // session binds to the effort the first send will carry (a blank task would
  // otherwise warm the platform default and a different first-send pick
  // would discard it).
  try {
    await apiFetch(`${API}/api/tasks/${encodeURIComponent(taskId)}/prewarm`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        model: options?.model ?? null,
        effort: options?.effort ?? null,
      }),
    });
  } catch (e) {
    log.debug("prewarm failed (non-fatal)", e);
  }
}

/** Stop an in-flight run.
 *
 * Resolves `{ cancelled }`. `false` means the Stop did NOT take — nothing was
 * running, or the turn is owned by another instance this request can't reach.
 * Callers MUST act on it: `apiFetch` only throws on 401, so a non-2xx does not
 * reject this promise either, and a caller that clears its "stopping" state
 * only in `.catch()` sits there forever while the agent keeps working. */
export async function cancelTaskRun(taskId: string): Promise<{ cancelled: boolean }> {
  const r = await apiFetch(`${API}/api/tasks/${encodeURIComponent(taskId)}/cancel`, {
    method: "POST",
  });
  if (!r.ok) return { cancelled: false };
  try {
    const body = (await r.json()) as { cancelled?: boolean };
    // Absent flag → assume it took, matching the older contract where any 2xx
    // meant the cancel was accepted.
    return { cancelled: body?.cancelled !== false };
  } catch {
    return { cancelled: true };
  }
}

/** Inject a steering message into an in-flight run.
 *
 * The text reaches the live agent as a `user.message`; the agent folds it
 * into the current turn (no new turn, no stream restart). The already-open
 * SSE stream delivers a `steer` event then the agent's response. Throws on
 * 409 when there's no active run to steer. */
export async function steerTaskRun(
  taskId: string,
  text: string,
  commentSlides?: Schemas["CommentSlideRef"][],
  analytics?: {
    source?: string;
    webSearch?: boolean;
    permissionMode?: string;
    model?: string;
    effort?: string;
    projectId?: string | null;
  },
  // Files to deliver into the live run with this steer (ENG-3222). The server
  // mounts them into the session immediately and hints the agent, so a file
  // that landed a beat behind its message reaches the agent THIS turn. `text`
  // may be empty when attachments are present (attachment-only delivery).
  attachmentFileIds?: string[],
): Promise<void> {
  const body: Record<string, unknown> = { text };
  if (commentSlides && commentSlides.length > 0) {
    body.comment_slides = commentSlides;
  }
  if (attachmentFileIds && attachmentFileIds.length > 0) {
    body.attachment_file_ids = attachmentFileIds;
  }
  await requestOk(`${API}/api/tasks/${encodeURIComponent(taskId)}/steer`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  // requestOk throws on a non-OK response, so reaching here means the steer was
  // accepted into the live run. A steer is still a user message, so count it for
  // the message_sent / activity-volume metrics (steer text is never directive-
  // prefixed, so web_search must be passed explicitly).
  trackMessageSent(taskId, text, {
    permissionMode: analytics?.permissionMode,
    model: analytics?.model,
    effort: analytics?.effort,
    // Steered files count toward attachment_count like a normal message's
    // (codex P2 on #1881) — including attachment-only auto-deliveries.
    attachmentFileIds,
    webSearch: analytics?.webSearch,
    source: analytics?.source ?? "steer",
    projectId: analytics?.projectId,
  });
}


// AgentEvent is consumed inside this module via the ChatStreamHandlers
// imports above. Re-export the type so callers that historically reached
// for it through `@/lib/api/tasks` keep working when the eventual
// per-resource imports land.
export type { AgentEvent };
